import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { saveCreatives } from '../../services/creativeLibraryService';

// ── Ad Launcher · Import de créatives (façon AdAmigo) ─────────────
// Zone drag & drop + parcourir + import Google Drive. Les fichiers partent
// sur Cloudinary (upload unsigned, même preset que le reste de l'app) puis
// sont enregistrés dans la Bibliothèque créas — réutilisables dans l'AI
// Launcher, Shopify Ads et les campagnes.
//
// Google Drive : activé par variables d'environnement CLIENT (à ajouter dans
// client/.env puis rebuild) :
//   REACT_APP_GDRIVE_API_KEY   → clé API navigateur (Google Cloud Console,
//                                Picker API activée)
//   REACT_APP_GDRIVE_CLIENT_ID → OAuth Client ID web avec l'origine
//                                https://onebotads.net autorisée
// Sans ces variables, le bouton explique quoi configurer au lieu de planter.

const CLOUD_NAME = 'dxtj7sllk';
const UPLOAD_PRESET = 'd5rr7y5s';
const MAX_SIZE_MB = 100;

const ACCEPTED = {
  'image/jpeg': 'image', 'image/png': 'image', 'image/webp': 'image',
  'video/mp4': 'video', 'video/quicktime': 'video', 'video/webm': 'video',
};

const GDRIVE_API_KEY = process.env.REACT_APP_GDRIVE_API_KEY;
const GDRIVE_CLIENT_ID = process.env.REACT_APP_GDRIVE_CLIENT_ID;

// Charge un script externe une seule fois.
const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Échec de chargement ${src}`));
    document.head.appendChild(s);
  });

const uploadToCloudinary = async (blob, kind, filename) => {
  const fd = new FormData();
  fd.append('file', blob, filename);
  fd.append('upload_preset', UPLOAD_PRESET);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/${kind}/upload`, { method: 'POST', body: fd });
  const data = await res.json();
  if (!data.secure_url) throw new Error(data.error?.message || 'Upload Cloudinary échoué');
  return data.secure_url;
};

const CreativeImport = ({ onImported }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(''); // "2/5 — fichier.mp4"

  const importFiles = async (fileList) => {
    const files = [...fileList];
    if (files.length === 0) return;

    // Validation type + taille AVANT tout upload.
    const valid = [];
    for (const f of files) {
      const kind = ACCEPTED[f.type];
      if (!kind) { toast.error(`${f.name} : format non supporté (JPG, PNG, WebP, MP4, MOV, WebM).`); continue; }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) { toast.error(`${f.name} : dépasse ${MAX_SIZE_MB} Mo.`); continue; }
      valid.push({ file: f, kind });
    }
    if (valid.length === 0) return;

    setUploading(true);
    const saved = [];
    try {
      for (let i = 0; i < valid.length; i++) {
        const { file, kind } = valid[i];
        setProgress(`${i + 1}/${valid.length} — ${file.name}`);
        const url = await uploadToCloudinary(file, kind, file.name);
        saved.push({ type: kind, url, prompt: `Import — ${file.name}`, source: 'import' });
      }
      await saveCreatives(saved);
      toast.success(`${saved.length} créative(s) importée(s) dans la bibliothèque ✓`);
      if (onImported) onImported();
    } catch (err) {
      toast.error(`Import interrompu : ${err.message}`);
      if (saved.length > 0 && onImported) onImported();
    } finally {
      setUploading(false);
      setProgress('');
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  // ── Google Drive (Picker) ──────────────────────────────────────
  const importFromDrive = async () => {
    // Le CLIENT_ID suffit (même OAuth client que Google Ads) — la clé API
    // navigateur est optionnelle (setDeveloperKey omis si absente).
    if (!GDRIVE_CLIENT_ID) {
      toast.info('Google Drive non configuré — ajoutez REACT_APP_GDRIVE_CLIENT_ID dans client/.env puis rebuildez.');
      return;
    }
    try {
      await loadScript('https://apis.google.com/js/api.js');
      await loadScript('https://accounts.google.com/gsi/client');
      await new Promise((r) => window.gapi.load('picker', r));

      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GDRIVE_CLIENT_ID,
        scope: 'https://www.googleapis.com/auth/drive.readonly',
        callback: (tokenResponse) => {
          const token = tokenResponse.access_token;
          const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS)
            .setMimeTypes(Object.keys(ACCEPTED).join(','));
          let builder = new window.google.picker.PickerBuilder()
            .setOAuthToken(token)
            .addView(view);
          if (GDRIVE_API_KEY) builder = builder.setDeveloperKey(GDRIVE_API_KEY);
          const picker = builder
            .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
            .setCallback(async (data) => {
              if (data.action !== window.google.picker.Action.PICKED) return;
              setUploading(true);
              const saved = [];
              try {
                const docs = data.docs || [];
                for (let i = 0; i < docs.length; i++) {
                  const doc = docs[i];
                  const kind = ACCEPTED[doc.mimeType];
                  if (!kind) { toast.error(`${doc.name} : format non supporté.`); continue; }
                  if (doc.sizeBytes && doc.sizeBytes > MAX_SIZE_MB * 1024 * 1024) { toast.error(`${doc.name} : dépasse ${MAX_SIZE_MB} Mo.`); continue; }
                  setProgress(`${i + 1}/${docs.length} — ${doc.name} (Drive)`);
                  // Télécharge le binaire depuis Drive puis ré-upload Cloudinary
                  // (les URLs Drive ne sont pas servables publiquement).
                  const res = await fetch(`https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!res.ok) throw new Error(`Drive: téléchargement refusé pour ${doc.name}`);
                  const blob = await res.blob();
                  const url = await uploadToCloudinary(blob, kind, doc.name);
                  saved.push({ type: kind, url, prompt: `Import Drive — ${doc.name}`, source: 'import' });
                }
                if (saved.length > 0) {
                  await saveCreatives(saved);
                  toast.success(`${saved.length} créative(s) importée(s) depuis Google Drive ✓`);
                  if (onImported) onImported();
                }
              } catch (err) {
                toast.error(`Import Drive interrompu : ${err.message}`);
              } finally {
                setUploading(false);
                setProgress('');
              }
            })
            .build();
          picker.setVisible(true);
        },
      });
      tokenClient.requestAccessToken();
    } catch (err) {
      toast.error(`Google Drive indisponible : ${err.message}`);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-6 mb-6">
      <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-3">📥 Ad Launcher — Importer des créatives</h3>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); importFiles(e.dataTransfer.files); }}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`rounded-2xl border-2 border-dashed px-6 py-10 text-center cursor-pointer transition ${
          dragOver ? 'border-[#221C51] bg-indigo-50' : 'border-gray-200 hover:border-[#221C51]/40 bg-gray-50'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={Object.keys(ACCEPTED).join(',')}
          className="hidden"
          onChange={(e) => importFiles(e.target.files)}
        />
        <div className="text-3xl mb-2">🎬</div>
        {uploading ? (
          <p className="text-sm font-semibold text-[#221C51] animate-pulse">Import en cours… {progress}</p>
        ) : (
          <>
            <p className="text-sm font-bold text-[#221C51]">Glissez-déposez vos créatives ici</p>
            <p className="text-xs text-gray-400 mt-1">ou choisissez une méthode d'import ci-dessous</p>
          </>
        )}

        <div className="flex items-center justify-center gap-2 mt-5" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-[#221C51] rounded-xl hover:bg-[#2d2570] shadow-sm transition disabled:opacity-50"
          >
            Parcourir les fichiers
          </button>
          <button
            type="button"
            onClick={importFromDrive}
            disabled={uploading}
            title={GDRIVE_CLIENT_ID ? 'Importer depuis Google Drive' : 'Configurer REACT_APP_GDRIVE_CLIENT_ID dans client/.env'}
            className={`px-5 py-2.5 text-sm font-semibold rounded-xl border transition disabled:opacity-50 ${
              GDRIVE_CLIENT_ID
                ? 'text-[#221C51] border-[#221C51]/30 hover:bg-[#221C51]/5'
                : 'text-gray-400 border-gray-200'
            }`}
          >
            <span className="mr-1.5">🗂️</span>Google Drive
          </button>
        </div>

        <div className="flex items-center justify-center gap-4 mt-4 text-[11px] text-gray-400">
          <span>JPG, PNG, WebP</span>
          <span>·</span>
          <span>MP4, MOV, WebM</span>
          <span>·</span>
          <span>Max {MAX_SIZE_MB} Mo / fichier</span>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between flex-wrap gap-2 rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 px-4 py-3">
        <p className="text-xs text-gray-600">
          <b className="text-[#221C51]">Besoin de nouvelles créas ?</b> Générez images, vidéos et avatars avec l'IA dans le Cinema Studio.
        </p>
        <button
          type="button"
          onClick={() => navigate('/dashboard/cinema-studio')}
          className="text-xs font-semibold text-white bg-fuchsia-600 rounded-lg px-3.5 py-2 hover:bg-fuchsia-700 transition"
        >
          🎬 Ouvrir le Cinema Studio →
        </button>
      </div>
    </div>
  );
};

export default CreativeImport;
